import { createContext, useContext, type PropsWithChildren } from "react";
import {
  type RoomDto,
  type RoomFormDto,
  roomsDtoSchema,
  type SessionDto,
  type SessionFormDto,
  sessionsDtoSchema,
  type SpeakerDto,
  type SpeakerFormDto,
  speakersDtoSchema,
  type TagDto,
  type TagFormDto,
  tagsDtoSchema,
} from "slde-api/dto-schemas";
import {
  useMutation,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { roomsApi } from "@/services/rooms/rooms.api";
import { $fetch } from "@/lib/fetch";
import { speakersApi } from "@/services/speakers/speakers.api";
import { tagsApi } from "@/services/tags/tags.api";
import { toast } from "sonner";
import { sessionsApi } from "@/services/sessions/sessions.api";
import type { SessionEntity } from "slde-api/db-schemas";

export type ResourcesContextType = {
  rooms: RoomDto[];
  tags: TagDto[];
  speakers: SpeakerDto[];
  sessions: SessionDto[];
  createSpeaker: (form: SpeakerFormDto) => void;
  isCreatingSpeaker: boolean;
  deleteSpeaker: ({ id }: { id: number }) => void;
  isDeletingSpeaker: boolean;
  createTag: (form: TagFormDto) => void;
  isCreatingTag: boolean;
  deleteTag: ({ id }: { id: number }) => void;
  isDeletingTag: boolean;
  createSession: (form: SessionFormDto) => void;
  isCreatingSession: boolean;
  deleteSession: ({ id }: { id: number }) => void;
  isDeletingSession: boolean;
};

export const ResourcesContext = createContext<ResourcesContextType | null>(
  null,
);

export interface ResourcesProviderProps {}

export function ResourcesProvider({
  children,
}: PropsWithChildren<ResourcesProviderProps>) {
  const queryClient = useQueryClient();

  const {
    data: { items: rooms },
  } = useSuspenseQuery({
    queryKey: roomsApi.rooms.queryKey(),
    queryFn: async () =>
      await $fetch(roomsApi.rooms.route(), {
        output: roomsDtoSchema,
        throw: true,
      }),
  });

  const { mutate: createSpeaker, isPending: isCreatingSpeaker } = useMutation({
    mutationFn: async (form: SpeakerFormDto) =>
      await $fetch(speakersApi.speakers.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Prelegenta utworzono pomyślnie");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu prelegenta: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: createTag, isPending: isCreatingTag } = useMutation({
    mutationFn: async (form: TagFormDto) =>
      await $fetch(tagsApi.tags.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Kategoria została dodana");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu kategorii: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: createSession, isPending: isCreatingSession } = useMutation({
    mutationFn: async (form: SessionFormDto) =>
      await $fetch(sessionsApi.sessions.route(), {
        method: "POST",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Sesja została dodana");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy dodawaniu sesji: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: updateSession, isPending: isUpdatingSession } = useMutation({
    mutationFn: async ({ id, ...form }: SessionEntity) =>
      await $fetch(sessionsApi.session.route(id), {
        method: "PATCH",
        body: form,
      }),
    onSuccess: () => {
      toast.success("Zaktualizowano sesje");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił bład przy aktualizacji sesji: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteSession, isPending: isDeletingSession } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(sessionsApi.session.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Sesja została usunięta");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu sesji: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteTag, isPending: isDeletingTag } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(tagsApi.tag.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Kategoria została usunięta");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu kategorii: ${e.message}`);
      }
      console.error(e);
    },
  });

  const { mutate: deleteSpeaker, isPending: isDeletingSpeaker } = useMutation({
    mutationFn: async ({ id }: { id: number }) =>
      await $fetch(speakersApi.speaker.route(id), {
        method: "DELETE",
      }),
    onSuccess: () => {
      toast.success("Prelegent został usunięty");
      void queryClient.invalidateQueries();
    },
    onError: (e) => {
      if (e instanceof Error) {
        toast.error(`Wystąpił błąd przy usuwaniu prelegenta: ${e.message}`);
      }
      console.error(e);
    },
  });

  const {
    data: { items: speakers },
  } = useSuspenseQuery({
    queryKey: speakersApi.speakers.queryKey(),
    queryFn: async () =>
      await $fetch(speakersApi.speakers.route(), {
        output: speakersDtoSchema,
        throw: true,
      }),
  });

  const {
    data: { items: tags },
  } = useSuspenseQuery({
    queryKey: tagsApi.tags.queryKey(),
    queryFn: async () =>
      await $fetch(tagsApi.tags.route(), {
        output: tagsDtoSchema,
        throw: true,
      }),
  });

  const {
    data: { items: sessions },
  } = useSuspenseQuery({
    queryKey: sessionsApi.sessions.queryKey(),
    queryFn: async () =>
      await $fetch(sessionsApi.sessions.route(), {
        output: sessionsDtoSchema,
        throw: true,
      }),
  });

  return (
    <ResourcesContext.Provider
      value={{
        rooms,
        tags,
        speakers,
        createSpeaker,
        isCreatingSpeaker,
        deleteSpeaker,
        isDeletingSpeaker,
        createTag,
        isCreatingTag,
        deleteTag,
        isDeletingTag,
        createSession,
        isCreatingSession,
        deleteSession,
        isDeletingSession,
        sessions,
      }}
    >
      {children}
    </ResourcesContext.Provider>
  );
}

export const useResources = () => {
  const context = useContext(ResourcesContext);
  if (!context)
    throw new Error("useResources must be used within an ResourcesProvider");
  return context;
};
